import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Constants from '../utils/Constants';
import fetchMolContent from '../services/contentService';
import resolveFrameOrigin, { frameSrc } from '../utils/frameOrigin';

const centerView = `
// --- Centering and Zooming (to be placed at the end of your script) ---

// 1. Reset the view (optional but recommended for consistency)
reset;

// 2. Select everything that is currently displayed (or select all if you want to ensure everything
// is included)
select displayed;

// 3. Center the view on the selection
center selected;

// 4. Set the zoom to 50%
zoom 50;

`;

// Path data for the export button's second icon shape. Written as pieces, split only
// where a path command starts, so no source line is over-long. Joining with '' gives
// back the original string exactly.
const EXPORT_ICON_PATH = [
  'M2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2',
  'h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828',
  'a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4H2zm.5 2',
  'a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm9 2.5',
  'a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0z',
].join('');

function createBlob(data) {
  if (typeof data === 'string') {
    return new Blob([data], { type: 'text/plain' });
  }
  return data;
}

function downloadImage(dataUrl, fileName) {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function MolViewer({
  molContent,
  viewType = 'mol',
  fnCb = () => {},
  fnInit = () => {},
  src = '',
  src2 = '',
  jmScripts = [],
  showExport = false,
  exportFileName = 'molecule.png',
}) {
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const [apiMolContent, setApiMolContent] = useState(null);

  // Where the viewer iframe is served from. `src2` allows hosting JSmol somewhere else;
  // with the default ('') this is our own origin. It is used to filter inbound messages
  // and to address outbound ones, so neither side has to trust '*'.
  //
  // Resolved on demand, from handlers and effects, never during render: it reads
  // `window`, which does not exist when this component is server-rendered.
  const getFrame = useCallback(() => {
    const frame = resolveFrameOrigin(src2, {
      baseURI: document.baseURI,
      href: window.location.href,
      origin: window.location.origin,
    });
    if (!frame) {
      console.error(
        `react-molviewer: cannot work out which origin serves ${frameSrc(src2)}, ` +
          'so messages to and from the viewer are being ignored.'
      );
    }
    return frame;
  }, [src2]);

  const handleMessage = useCallback(
    event => {
      // Full-origin comparison. Matching on hostname alone accepted messages from
      // any other port on this host, and from http:// against an https:// page.
      const frame = getFrame();
      if (!frame || event.origin !== frame.origin) {
        return;
      }
      const { type, data, imageData } = event.data;
      if (type === Constants.POST.TYPE) {
        if (data === Constants.POST.COMPLETE) {
          fnCb();
        } else if (data === Constants.POST.START) {
          fnInit();
        } else if (data === Constants.POST.IMAGE_DATA && imageData) {
          downloadImage(imageData, exportFileName);
        }
      }
    },
    [fnInit, fnCb, exportFileName, getFrame]
  );

  useEffect(() => {
    fnInit();
    window.addEventListener(Constants.EVENT.MESSAGE, handleMessage);
    return () => {
      window.removeEventListener(Constants.EVENT.MESSAGE, handleMessage);
    };
  }, [handleMessage, fnInit]);

  useEffect(() => {
    const loadMolContent = async () => {
      if (isIframeLoaded && viewType) {
        if (/^file/.test(viewType)) {
          setApiMolContent(molContent);
        } else if (/^mol/.test(viewType)) {
          try {
            const fetchedMolContent = await fetchMolContent(src, molContent);
            setApiMolContent(createBlob(fetchedMolContent));
          } catch (error) {
            console.log('error=', error);
            setApiMolContent(createBlob(molContent));
          }
        }
      }
    };
    loadMolContent();
  }, [viewType, isIframeLoaded, src, molContent]);

  useEffect(() => {
    if (!apiMolContent) return;

    const iframe = document.getElementById(Constants.IFRAME.ELEMENT_ID);
    let runScript =
      'set echo top left;font echo 18 arial italic;echo "loading file...processing...';
    runScript +=
      'this might take a while if the file contains a complex structure.";refresh;';

    const postMessage = script => {
      const frame = getFrame();
      if (!frame) return;
      iframe.contentWindow.postMessage(
        { type: Constants.POST.TYPE, data: script },
        frame.target
      );
    };

    if (/^file/.test(viewType)) {
      runScript += ` load async "${apiMolContent}"; `;
      runScript += jmScripts.join(' ');
      runScript += ' echo ""; ';
      runScript += centerView;
      postMessage(runScript);
    } else if (/^mol/.test(viewType)) {
      apiMolContent.text().then(text => {
        runScript += ` load DATA "mol_example"\n${text}\nend "mol_example"; `;
        runScript += jmScripts.join(' ');
        runScript += 'show data; echo ""; ';
        runScript += centerView;
        postMessage(runScript);
      });
    }
  }, [apiMolContent, jmScripts, viewType, getFrame]);

  const handleExportImage = () => {
    const iframe = document.getElementById(Constants.IFRAME.ELEMENT_ID);
    const frame = iframe && iframe.contentWindow ? getFrame() : null;
    if (frame) {
      iframe.contentWindow.postMessage(
        {
          type: Constants.POST.TYPE,
          action: Constants.ACTION.EXPORT_IMAGE,
        },
        frame.target
      );
    }
  };

  if (!/^file|^mol/.test(viewType)) {
    return <span>{Constants.MESSAGE.NOT_SUPPORTED}</span>;
  }
  if (!molContent) {
    return <span>{Constants.MESSAGE.NO_CONTENT}</span>;
  }

  return (
    <div className="structure-frame-container">
      {showExport && (
        <button
          type="button"
          className="molviewer-export-btn"
          onClick={handleExportImage}
          title="Export as image"
          aria-label="Export molecule view as PNG image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M10.5 8.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
            <path d={EXPORT_ICON_PATH} />
          </svg>
        </button>
      )}
      <iframe
        id={Constants.IFRAME.ELEMENT_ID}
        src={frameSrc(src2)}
        title="Molecule Viewer Web"
        className="frame-div"
        onLoad={() => setIframeLoaded(true)}
      />
    </div>
  );
}

MolViewer.propTypes = {
  molContent: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
    .isRequired,
  viewType: PropTypes.string,
  fnCb: PropTypes.func,
  fnInit: PropTypes.func,
  src: PropTypes.string,
  src2: PropTypes.string,
  jmScripts: PropTypes.arrayOf(PropTypes.string),
  showExport: PropTypes.bool,
  exportFileName: PropTypes.string,
};

MolViewer.defaultProps = {
  viewType: 'mol',
  fnCb: () => {},
  fnInit: () => {},
  src: '',
  src2: '',
  jmScripts: [],
  showExport: false,
  exportFileName: 'molecule.png',
};

export default MolViewer;
